import { useState, useEffect } from 'react';
import { Stack, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { MODULES_ROLES_LIST, GET_SIDEBAR_FUNCTIONS_BY_ROLE } from '../../../URLs';
import { useData } from '../../../../App';
import { getLocalStorageItem } from '../../../localStorageUtils';
import { tabsConstants } from '../../../sideBarConstants';
import { useNavigate } from "react-router-dom";
import { HELPDESK_ROLES, PAYMENT_ROLES, QUALITY_ROLES, ROLES, RESOURCE_ROLES, PIT_ROLES, DEMAND_ROLES } from '../../../constants';
import apiClient from '../../../../axios';

export default function ModuleRoleDropdown() {
  const navigate = useNavigate();
  const { setSideBarData } = useData();

  const [module, setModule] = useState("");
  const [role, setRole] = useState("");
  const [modulesList, setModulesList] = useState([]);
  const [rolesList, setRolesList] = useState([]);

  //  store module when it changes
  useEffect(() => {
    if (module) localStorage.setItem('module', module);
  }, [module]);

  //  helper: always store home_path + navigate with replace (no bad history)
  const go = (path) => {
    localStorage.setItem("home_path", path);
    navigate(path, { replace: true });
  };

  //  navigate by role (and store home_path)
  const navigateToPage = () => {
    switch (role) {
      case ROLES.ItAdmin:
        go('/sidebar/onboardingStakeholders'); break;

      case ROLES.RegistrationScruitnizer:
      case ROLES.LeadRegScrutinizer:
      case ROLES.RegInCharge:
        go('/registrationHome/dashboard'); break;

      case ROLES.RegAssistant:
        go('/registrationHome/preregistration'); break;

      case ROLES.RegSuperVisor:
        go('/registrationHome/supervisorDashboard'); break;

      case ROLES.RegSCMUser:
        go('/registrationHome/registration'); break;

      case ROLES.SCMInCharge:
        go('/userOnboardingApproval'); break;

      case ROLES.UserOnboarding:
        go('/sidebar/onboardingStakeholders'); break;

      case PAYMENT_ROLES.SCMEndUser:
      case PAYMENT_ROLES.PaymentApprover:
      case PAYMENT_ROLES.FinanceUser:
      case PAYMENT_ROLES.PaymentViewer:
        go('/paymentDashboard/advancePayment'); break;

      case PAYMENT_ROLES.Updater:
        go('/paymentDashboard/PaymentStatusABRPLUser'); break;

      case QUALITY_ROLES.ScmUser:
      case QUALITY_ROLES.QualityModuleIncharge:
        go('/qualitydashboard/home'); break;

      case QUALITY_ROLES.Inspector:
        go('/qualitydashboard/inspectionworkorder'); break;

      case HELPDESK_ROLES.HelpdeskAdminUser:
      case HELPDESK_ROLES.SMUSer:
      case HELPDESK_ROLES.ExternalAdminUser:
        go('/helpDeskDashboard/helpDeskDashboard'); break;

      case HELPDESK_ROLES.HelpdeskSupportExecutive:
      case HELPDESK_ROLES.HelpdeskAssistant:
      case HELPDESK_ROLES.ExternalSupportExecutive:
        go('/helpDeskDashboard/requests'); break;

      case RESOURCE_ROLES.ResourceScrutinizer:
      case RESOURCE_ROLES.PlantSCMMember:
      case RESOURCE_ROLES.FinanceUser:
      case RESOURCE_ROLES.CommercialUser:
      case RESOURCE_ROLES.ModuleIncharge:
      case RESOURCE_ROLES.Approver:
      case RESOURCE_ROLES.ResSupervisor:
      case RESOURCE_ROLES.ResPlantationManager:
        go('/resourcedashboard/home'); break;

      case PIT_ROLES.PitUnloader:
      case PIT_ROLES.PitWeighbridgeOperator:
      case PIT_ROLES.PitManualWeighbridgeOperator:
        go('/asnGRN'); break;

      case PIT_ROLES.PitApprover:
      case PIT_ROLES.ProcessingIncharge:
      case PIT_ROLES.ProcurementLead:
        go('/pitWorkflowDashboard'); break;

      case PIT_ROLES.Incharge:
        go('/spotBiddingApproval'); break;

      case PIT_ROLES.PlantSCMOperator:
        go('/dbsScheduleCreation'); break;

      case PIT_ROLES.PitScmUser:
      case PIT_ROLES.TransportationIncharge:
        go('/trDashboard'); break;

      case PIT_ROLES.PitProcessingPlant:
      case PIT_ROLES.PitProcessingLLE:
        go('/rbScheduleApproval'); break;

      case PIT_ROLES.Loader:
        go('/consignmentPickupTracker'); break;

      case PIT_ROLES.LLESCMOperator:
        go('/lleScheduleCreation'); break;

      case DEMAND_ROLES.DemandPlanner:
        go('/demandDashboard/yearlyInput'); break;

      case DEMAND_ROLES.DemandModuleIncharge:
        go('/demandDashboard/monthlyAllocation'); break;

      default:
        // fallback
        go('/sideBar');
    }
  };

  //  On first load: fetch modules/roles and RESTORE saved module/role if present
  useEffect(() => {
    const loginID = localStorage.getItem('uuid');

    apiClient.post(
      MODULES_ROLES_LIST,
      { loginId: loginID },
      {
        headers: { Authorization: "Bearer " + getLocalStorageItem('id_token') }
      }
    ).then((response) => {
      const list = response?.data?.lstModulesWithRolesMaster || [];
      setModulesList(list);

      const savedModule = localStorage.getItem("module");
      const savedRole = localStorage.getItem("role");

      const selectedModuleObj =
        list.find(m => m.parentModuleName === savedModule) || list[0];

      const selectedRoles = selectedModuleObj?.childRolesName || [];

      const selectedRole =
        selectedRoles.find(r => r.childRolesName === savedRole)?.childRolesName ||
        selectedRoles[0]?.childRolesName ||
        "";

      setModule(selectedModuleObj?.parentModuleName || "");
      setRolesList(selectedRoles);
      setRole(selectedRole);
    }).catch(console.log);
  }, []);

  // When role changes:
  // - store role
  // - fetch sidebar functions
  // - navigate to page (and store home_path)
  useEffect(() => {
    if (!role) return;

    if (role !== localStorage.getItem('role')) {
      localStorage.setItem('role', role);
    }

    const roleId = rolesList?.find((item) => item.childRolesName === role)?.roleId;
    if (!roleId) {
      navigateToPage();
      return;
    }

    apiClient.post(
      GET_SIDEBAR_FUNCTIONS_BY_ROLE,
      { roleId },
      { headers: { Authorization: "Bearer " + getLocalStorageItem('id_token') } }
    ).then((response) => {
      setSideBarData(
        response?.data?.lstFunctionMaster.map(obj => {
          const { parentFunctionName, childFunctionName } = obj;
          const matchedParent = tabsConstants.find(c => c.functionName === parentFunctionName);
          const matchedChildren = childFunctionName
            ? tabsConstants.filter(child => childFunctionName.some(c => c === child.functionName))
            : null;

          return {
            ...matchedParent,
            children: matchedChildren && matchedChildren.length > 0 ? [...matchedChildren] : null
          };
        })
      );

      navigateToPage();
    }).catch(console.log);

  }, [role]);

  const handleChange = (e) => {
    if (e.target.name === "module") {
      const newModule = e.target.value;
      setModule(newModule);

      const newRolesList = modulesList.find((item) => item.parentModuleName === newModule)?.childRolesName || [];
      setRolesList(newRolesList);

      const firstRole = newRolesList[0]?.childRolesName || "";
      setRole(firstRole);
    }

    if (e.target.name === "role") {
      setRole(e.target.value);
    }
  };

  return (
    <Stack spacing={1} direction="row" sx={{ minWidth: 0 }}>
      <FormControl size="small">
        <InputLabel id="module-label">Module</InputLabel>
        <Select
          labelId="module-label"
          label="Module"
          name="module"
          value={module}
          onChange={handleChange}
        >
          {modulesList?.map((item) => (
            <MenuItem key={item.parentModuleName} value={item.parentModuleName}>
              {item.parentModuleName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel id="role-label">Role</InputLabel>
        <Select
          labelId="role-label"
          label="Role"
          name="role"
          value={role}
          onChange={handleChange}
        >
          {rolesList?.map((item) => (
            <MenuItem key={item?.roleId || item?.childRolesName} value={item?.childRolesName}>
              {item?.childRolesName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}